import { lazy, Suspense, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaApple, FaBookOpen, FaPlusCircle, FaMoneyBillWave, FaPiggyBank,
  FaRupeeSign, FaPlus, FaChevronDown,
} from 'react-icons/fa';

import { IoLogoAndroid } from 'react-icons/io';
import { FiMenu } from 'react-icons/fi';
import { RiCustomerService2Fill } from 'react-icons/ri';
import { brand, useBrandFeatures, useBrandLogo, useSiteSettings } from '@/brands';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { openAuthModal, setAgeModalOpen, setRulesModalOpen } from '@/store/slices/uiSlice';
import { useGetUserInfoQuery, useGetUserWalletsQuery } from '@/store/api/userApi';
import { useGetApkConfigQuery } from '@/store/api/systemApi';
import { useGetNotificationsQuery } from '@/store/api/contentApi';
import { isNativeApp } from '@/utils/platform';
import { formatNumber } from '@/utils';

import UserDropdown from './UserDropdown';
import Navbar from '@/components/Navbar';
import HeaderTopGames from './HeaderTopGames';
import SetButtonValueModal from './SetButtonValueModal';
import RulesModal from '@/components/RulesModal';
import AgeRestrictionModal from '@/components/AgeRestrictionModal';
import Marquee from '@/components/Marquee';
import SearchDropdown from '@/components/common/SearchDropdown';
import MyMarketModal from './MyMarketModal';
import DepositModal from '@/components/deposit/DepositModal';

// Split out of the header bundle: it carries the help-topic index, and most
// visits never open it.
const SupportPanel = lazy(() => import('@/components/support/SupportPanel'));

/**
 * Site header.
 *
 * Three stacked bands, as on the reference site:
 *
 *   1. a white bar carrying the drawer button, a support (headset) button, the
 *      wordmark, and — depending on whether anyone is signed in — either the
 *      "our applications" icons or the balance and account menu;
 *   2. a white band with the two sign-in buttons (or the cashier row for a
 *      signed-in player) and the search field;
 *   3. the product navigation, which is unchanged.
 *
 * Band 1 is white with dark glyphs, as on the reference; the wordmark keeps its
 * own dark plate, which is what makes it the one solid shape on the bar.
 */
const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const logo = useBrandLogo();
  const [showBalance, setShowBalance] = useState(true);
  const [showExposure, setShowExposure] = useState(true);
  const [showMyMarketModal, setShowMyMarketModal] = useState(false);
  // Amount first, rails second — the sheet hands `/user/deposit` an ?amount=.
  const [depositOpen, setDepositOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const location = useLocation();
  const reportsPage = location.pathname.includes('user');
  // CRASH and the markets page show the header only down to the search row.
  const isCrashPage = location.pathname.toUpperCase().includes('CRASH');
  const isGameDetailsPage = location.pathname.includes('game-details');
  // Lobbies that bring their own category strip — the popular-events row is
  // noise on top of one.
  const isQtechPage = location.pathname.includes('qtech');
  const isFantasyPage = location.pathname.includes('fantasy-list');
  const isLiveCasinoPage = location.pathname.includes('live-casino');
  const isSlotsPage = location.pathname.includes('slots');
  const isCasinoPage = location.pathname.includes('casino');
  // Both run SportsBoard, which brings its own search over the match list.
  const isLivePage =
    location.pathname === '/inplay' || location.pathname.startsWith('/all-sports');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ageModalOpen } = useAppSelector((state) => state.ui);
  const isAuthed = useAppSelector((state) => state.auth.isAuthenticated);

  // Cashier buttons are a per-brand switch; the routes behind them are unmounted
  // in App.tsx when it is off, so hiding these keeps the header from dead-ending.
  const { deposit: cashierOn, signup: signupOn, showLogin: loginOn } = useSiteSettings();
  // The Site Setting above is one switch for the whole cashier; the super
  // admin's Features split it per direction, so a brand can take deposits while
  // withdrawals are paused (or run FD without either). Both have to pass.
  const { deposit: depositOn, withdrawal: withdrawalOn, fd: fdOn, registration } = useBrandFeatures();
  const showDeposit  = cashierOn && depositOn;
  const showWithdraw = cashierOn && withdrawalOn;
  const showFd       = cashierOn && fdOn;
  const canRegister  = signupOn && registration;

  // Polling two authenticated endpoints every 5s for a visitor who has not
  // signed in cost a pair of 401s a second and filled the console with them.
  useGetUserInfoQuery(undefined, { pollingInterval: 5000, skip: !isAuthed });
  useGetUserWalletsQuery(undefined, { pollingInterval: 5000, skip: !isAuthed });

  const { info, walletInfo } = useAppSelector((state) => state.user);
  const username = info?.username ?? 'Demo';
  const isDemoUser = info?.role?.name?.toLowerCase() == 'demo';
  const mainWallet = walletInfo?.find((item) => item?.type == 'Main');
  const mainBalance =
    Number(mainWallet?.amount ?? 0) -
    Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
    Number(mainWallet?.lockedAmount ?? 0);
  const exposure = mainWallet?.exposureAmount;

  return (
    <>
      <style>{HEADER_CSS}</style>

      <div className="hdr">

        {/* ── BAND 1 — white bar ── */}
        <div className="hdr-r1">

          <div className="hdr-left">
            <button className="hdr-ibtn hdr-menu" onClick={onMenuClick} aria-label="Menu">
              <FiMenu size={26} strokeWidth={1.5} />
            </button>
            <SupportButton onOpen={() => setSupportOpen(true)} />
            <Link to="/home" className="hdr-logo hdr-logo-desk">
              <img src={logo} alt={brand.name} />
            </Link>
          </div>

          {/* Centred wordmark — mobile only. Centred between the two side
              groups rather than in the bar as a whole: absolute centring put it
              under the balance the moment the sides stopped matching in width. */}
          <Link to="/home" className="hdr-logo hdr-logo-mob">
            <img src={logo} alt={brand.name} />
          </Link>

          {/* Desktop centre — search and the ticker. */}
          <div className="hdr-center">
            <div style={{ width: 300, flexShrink: 0 }}>
              <SearchDropdown variant="compact" />
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Marquee />
            </div>
          </div>

          <div className="hdr-right">
            {isAuthed ? (
              <>
                {showDeposit && (
                  <button className="hdr-dw dep" onClick={() => setDepositOpen(true)}>
                    <FaPlusCircle size={12} />
                    Deposit
                  </button>
                )}
                {showWithdraw && (
                  <button className="hdr-dw wd" onClick={() => navigate('/user/deposit-withdraw')}>
                    <FaMoneyBillWave size={12} />
                    Withdraw
                  </button>
                )}
                {showFd && (
                  <button className="hdr-dw fd" onClick={() => navigate('/user/fd')}>
                    <FaPiggyBank size={12} />
                    FD
                  </button>
                )}

                <button className="hdr-rules" onClick={() => dispatch(setRulesModalOpen(true))}>
                  <FaBookOpen size={12} />
                  Rules
                </button>

                {/* Phone cluster — inbox, the balance pill and the cashier's
                    "+", as on the reference. Desktop keeps the wider block
                    below it: there the row has the width for named buttons, and
                    collapsing a signed-in player's Deposit / Withdraw / Rules
                    into one "+" would be a downgrade on that screen. */}
                <div className="hdr-acct-mob">
                  <InboxButton />
                  {/* Balance and "+" read as one control, so they share one
                      white capsule — but they stay two buttons: the balance
                      opens the account menu, the "+" opens the cashier. */}
                  <div className="hdr-wallet">
                    <UserDropdown
                      isDemoUser={isDemoUser}
                      showBalance={showBalance}
                      showExposure={showExposure}
                      handleBalanceChange={setShowBalance}
                      handleExposureChange={setShowExposure}
                      username={username}
                    >
                      <button className="hdr-pill" aria-label="Account and balance">
                        <span className="hdr-pill-coin" aria-hidden="true">
                          <FaRupeeSign size={8} />
                        </span>
                        <span className="hdr-pill-amt">
                          {showBalance ? `₹ ${formatNumber(mainBalance)}` : '₹ ••••'}
                        </span>
                        <FaChevronDown size={10} className="hdr-pill-caret" />
                      </button>
                    </UserDropdown>
                    {showDeposit && (
                      <button
                        className="hdr-plus"
                        onClick={() => setDepositOpen(true)}
                        aria-label="Deposit"
                      >
                        <FaPlus size={12} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="hdr-acct">
                  {showBalance && (
                    <div className="hdr-bal">
                      Balance:<b>{formatNumber(mainBalance)}</b>
                    </div>
                  )}

                  <div className="hdr-exp-row">
                    {showExposure && (
                      <span className="hdr-exp">
                        Exp:<b>{exposure ?? 0}</b>
                      </span>
                    )}

                    <UserDropdown
                      isDemoUser={isDemoUser}
                      showBalance={showBalance}
                      showExposure={showExposure}
                      handleBalanceChange={setShowBalance}
                      handleExposureChange={setShowExposure}
                      username={username}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <AppLinks />
                {/* Desktop keeps the sign-in pair up here; on a phone it gets
                    the full-width band below, which is where the reference
                    puts it. */}
                <div className="hdr-auth-desk">
                  {loginOn && (
                    <button className="hdr-auth-btn login" onClick={() => dispatch(openAuthModal('login'))}>
                      Log in
                    </button>
                  )}
                  {canRegister && (
                    <button className="hdr-auth-btn reg" onClick={() => dispatch(openAuthModal('register'))}>
                      Registration
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Account quick-nav — signed-in players only; a visitor has none of
            these pages, and the routes behind them are protected.
            Deposit opens the amount sheet rather than jumping to the rails. */}
        {isAuthed && (
          <nav className="hdr-quick">
            <button onClick={() => navigate('/user/profile')}>Profile</button>
            {showDeposit && <button onClick={() => setDepositOpen(true)}>Deposit</button>}
            <button onClick={() => navigate('/user/bet-history')}>Bets history</button>
            <button onClick={() => navigate('/user/wallet-history')}>Payments history</button>
          </nav>
        )}

        {/* ── BAND 2 — white ground ── */}
        <div className="hdr-below">
          {!isAuthed && (loginOn || canRegister) && (
            <div className="hdr-auth-row">
              {loginOn && (
                <button className="hdr-auth-btn login" onClick={() => dispatch(openAuthModal('login'))}>
                  Log in
                </button>
              )}
              {canRegister && (
                <button className="hdr-auth-btn reg" onClick={() => dispatch(openAuthModal('register'))}>
                  Registration
                </button>
              )}
            </div>
          )}

          {/* Pages that bring their own search do not get this one too. On a
              lobby that search looks through the GAME catalog, which is what a
              player there means by "search"; on the live page it filters the
              match list. This one only ever looked through sports events, so on
              those pages it was both the wrong search and a second search bar
              on the screen. */}
          {!isCasinoPage && !isSlotsPage && !isLiveCasinoPage && !isQtechPage && !isLivePage && (
            <div className="hdr-r2">
              <SearchDropdown variant="pill" />
            </div>
          )}

          <div className="hdr-marq-mob">
            <Marquee type="mobile" />
          </div>
        </div>


        {/* ── BAND 3 — product navigation ── */}
        {!reportsPage && !isCrashPage && !isFantasyPage && !isGameDetailsPage && (
          <div className="hdr-bot">
            {!isQtechPage && !isFantasyPage && !isLiveCasinoPage && !isSlotsPage && !isCasinoPage && <HeaderTopGames />}
            <Navbar />
          </div>
        )}

        <SetButtonValueModal />
        <RulesModal />
        <AgeRestrictionModal open={ageModalOpen} onClose={() => dispatch(setAgeModalOpen(false))} />
        <MyMarketModal open={showMyMarketModal} onClose={() => setShowMyMarketModal(false)} />
        <DepositModal open={depositOpen} onClose={() => setDepositOpen(false)} />
        {supportOpen && (
          <Suspense fallback={null}>
            <SupportPanel open onClose={() => setSupportOpen(false)} />
          </Suspense>
        )}
      </div>
    </>
  );
};

/**
 * Support, beside the drawer button.
 *
 * Always drawn — the reference header carries the bubble whether or not the
 * operator has staffed a channel yet. It opens the support panel, which is
 * where the choice of channel is made.
 */
function SupportButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button className="hdr-ibtn hdr-chat" onClick={onOpen} aria-label="Support">
      <RiCustomerService2Fill size={23} />
    </button>
  );
}

/**
 * The "our applications" pair.
 *
 * Apple is always drawn, as on the reference header. There is no iOS build to
 * link to, so it installs the PWA when the browser has offered a prompt and
 * otherwise opens the Add-to-Home-Screen tip — which is the whole of what an
 * iPhone can do here, and the reason a plain link would be a dead button.
 * Android stays gated on the admin having uploaded an APK and switched the
 * download on, since that one really does lead nowhere without a file. Both are
 * hidden inside the APK itself, where installing the app again is meaningless.
 */
function AppLinks() {
  const inApp = isNativeApp();
  const { data: apk } = useGetApkConfigQuery(undefined, { skip: inApp });
  const { canInstall, handleInstall } = usePWAInstall();
  const [tip, setTip] = useState(false);

  if (inApp) return null;

  const showApk = !!apk?.isEnabled && !!apk?.apkUrl;

  return (
    <div className="hdr-apps">
      <div className="hdr-app-ios">
        <button
          className="hdr-ibtn"
          onClick={() => (canInstall ? handleInstall() : setTip((v) => !v))}
          aria-label="Install app"
        >
          <FaApple style={{ fontSize: 22 }} />
        </button>
        {tip && (
          <>
            {/* Catches the next tap anywhere so the tip closes like a menu. */}
            <div className="hdr-app-tip-veil" onClick={() => setTip(false)} />
            <div className="hdr-app-tip" role="tooltip">
              On iPhone: tap <strong>Share</strong>, then{' '}
              <strong>Add to Home Screen</strong>.
            </div>
          </>
        )}
      </div>
      {showApk && (
        <a
          className="hdr-ibtn"
          href={apk!.apkUrl}
          download
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Android app"
        >
          <span className="hdr-app-droid">
            <IoLogoAndroid style={{ fontSize: 23 }} />
            {/* The reference tags its Android link as new. */}
            <span className="hdr-app-new" aria-hidden="true">NEW</span>
          </span>
        </a>
      )}
    </div>
  );
}

/**
 * The header's envelope: the brand's Information notices.
 *
 * Same feed the ticker reads, on its other channel — Alerts scroll past in the
 * marquee, Information waits here to be opened. Drawn whether or not anything
 * has been posted: it is a fixed part of the signed-in header on the reference,
 * and an icon that comes and goes as the operator posts moves everything beside
 * it. An empty list says so rather than opening a blank card.
 */
/**
 * The inbox envelope, from the artwork supplied.
 *
 * Two paths: the body and the flap folded over it. Together they measure
 * x 0–20, y 0–15.45 — that extent is the viewBox, not a 20×20 square, because
 * the artwork is wider than it is tall and squaring the box would have left the
 * glyph floating above the bottom of its button.
 *
 * Painted in currentColor, not the artwork's white: this bar is yellow, and
 * white would have made the icon invisible on it.
 */
const ENVELOPE_PATH =
  'M12.3083 9.28104C11.6569 9.81356 10.8414 10.1045 10.0001 10.1045C9.1587 10.1045 8.34321 9.81356 7.69181 9.28104L0.0630367 3.01428C0.0239841 3.21945 0.00289199 3.42763 0 3.63646V11.8183C0.00102755 12.7824 0.384476 13.7068 1.06621 14.3885C1.74794 15.0702 2.67227 15.4537 3.63639 15.4547H16.3637C17.3279 15.4537 18.2522 15.0702 18.9339 14.3885C19.6156 13.7068 19.9991 12.7824 20.0001 11.8183V3.63646C19.9972 3.42763 19.9761 3.21945 19.9371 3.01428L12.3083 9.28104Z';

const ENVELOPE_FLAP_PATH =
  'M8.84598 7.87558C9.17156 8.14211 9.57935 8.28774 10.0001 8.28774C10.4209 8.28774 10.8287 8.14211 11.1542 7.87558L19.1399 1.31576C18.8022 0.905972 18.3784 0.575622 17.8986 0.348208C17.4188 0.120795 16.8948 0.0019068 16.3638 0H3.63643C3.10545 0.0019068 2.58142 0.120795 2.1016 0.348208C1.62178 0.575622 1.19797 0.905972 0.860313 1.31576L8.84598 7.87558Z';

const EnvelopeIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={(size * 20) / 15.455}
    height={size}
    viewBox="0 0 20 15.455"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d={ENVELOPE_PATH} />
    <path d={ENVELOPE_FLAP_PATH} />
  </svg>
);

function InboxButton() {
  const [open, setOpen] = useState(false);
  const { data } = useGetNotificationsQuery({ type: 'Information' });
  // The payload is keyed by the type asked for; only `Alert` is in the declared
  // shape, so this one is read off the same object by name.
  const notices =
    ((data?.data as Record<string, { id: number; title: string; body: string; isActive: boolean }[]>)
      ?.Information ?? []).filter((n) => n.isActive);

  return (
    <div className="hdr-inbox">
      <button
        className="hdr-ibtn"
        onClick={() => setOpen((v) => !v)}
        aria-label={notices.length ? `Messages, ${notices.length} unread` : 'Messages'}
      >
        <EnvelopeIcon size={15} />
        {notices.length > 0 && (
          <span className="hdr-inbox-badge" aria-hidden="true">
            {notices.length > 99 ? '99+' : notices.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Catches the next tap anywhere so the panel closes like a menu. */}
          <div className="hdr-inbox-veil" onClick={() => setOpen(false)} />
          <div className="hdr-inbox-panel" role="dialog" aria-label="Messages">
            {notices.length === 0 ? (
              <p className="hdr-inbox-empty">No messages yet.</p>
            ) : (
              notices.map((n) => (
                <article key={n.id} className="hdr-inbox-item">
                  <p className="hdr-inbox-title">{n.title}</p>
                  <p className="hdr-inbox-body">{n.body}</p>
                </article>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

const HEADER_CSS = `
  .hdr {
    position: sticky;
    top: 0;
    z-index: 50;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    background: #fff;
    box-shadow: 0 1px 0 rgba(52,49,49,0.10);
  }
  .hdr *, .hdr *::before, .hdr *::after { box-sizing: border-box; }

  /* ── BAND 1 ── */
  .hdr-r1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    /* Fixed height rather than vertical padding: the band's tallest child is
       the logo plate, so padding made the bar's height follow the wordmark
       instead of the other way round. Centring does the spacing now. */
    height: 44px;
    padding: 0 10px;
    width: 100%;
    /*
     * White bar with dark glyphs, as on the reference. Foreground and
     * background both run off the tokens below so changing one cannot leave
     * the other behind.
     */
    --hdr-fg: var(--cb-ink, #343131);
    --hdr-veil: rgba(0,0,0,.06);
    --hdr-veil-hi: rgba(0,0,0,.12);
    background: #fff;
    color: var(--hdr-fg);
  }

  .hdr-left  { display: flex; align-items: center; gap: 2px; flex: 0 0 auto; min-width: 62px; }
  .hdr-right {
    display: flex; align-items: center; justify-content: flex-end; gap: 8px;
    flex: 0 0 auto; min-width: 62px;
  }

  .hdr-ibtn {
    padding: 3px;
    background: transparent;
    border: none;
    display: flex; align-items: center; justify-content: center;
    color: var(--hdr-fg);
    cursor: pointer;
    flex-shrink: 0;
    transition: transform .15s, opacity .15s;
  }
  /* The drawer button carries --cb-ink rather than the band's --hdr-fg: it is
     the one glyph on the bar that reads as a control rather than a mark, and
     the softer ink is what separates it from the wordmark beside it. */
  .hdr-menu { color: var(--cb-ink, #343131); }

  /* The headset sits straight on the bar — no disc, no border. Padding comes
     from .hdr-ibtn so this button and the drawer's line up on one baseline. */
  .hdr-chat {
    background: none;
    color: var(--cb-ink, #343131);
  }

  .hdr-ibtn:hover  { opacity: .8; }
  .hdr-ibtn:active { transform: scale(.9); }

  .hdr-apps { display: flex; align-items: center; gap: 2px; }
  .hdr-app-ios { position: relative; display: flex; align-items: center; }
  /* Android glyph with the reference's yellow "NEW" tag under its feet. */
  .hdr-app-droid { position: relative; display: flex; align-items: center; justify-content: center; }
  .hdr-app-new {
    position: absolute;
    left: 50%;
    bottom: -5px;
    transform: translateX(-50%);
    padding: 1px 3px;
    border-radius: 3px;
    background: var(--cb-yellow, #ffb700);
    color: #fff;
    font-size: 7px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: .02em;
  }
  /* Full-screen catcher, under the tip but over everything else. */
  .hdr-app-tip-veil {
    position: fixed;
    inset: 0;
    z-index: 60;
  }
  .hdr-app-tip {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 61;
    width: max-content;
    max-width: 220px;
    padding: 8px 10px;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 400;
    color: #1f2937;
    text-align: left;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.10);
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.18);
  }
  .hdr-app-tip strong { font-weight: 700; }

  /*
   * Logo ke peeche gehra takht.
   *
   * Reference bhi apne logo ko aise hi ek gehre khaane me rakhta hai; safed
   * bar par yahi bar ka akela bhara hua aakaar hai.
   */
  .hdr-logo {
    display: inline-flex; align-items: center;
    background: var(--cb-ink-strong, #111110);
    border-radius: var(--cb-radius-s, .5rem);
    padding: 3px 8px;
    line-height: 0;
  }
  .hdr-logo img { width: auto; height: 40px; object-fit: contain; display: block; }
  .hdr-logo-desk { display: none; }
  .hdr-logo-mob {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    justify-content: center;
  }
  /* 98 × 19 on a phone. Height AND a width cap, not a fixed width: this
     brand's wordmark is a different shape from the reference's, and pinning
     both would letterbox it inside the plate — object-fit contain above keeps it
     whole, and the cap keeps it from eating the bar. */
  .hdr-logo-mob img { height: 19px; max-width: 98px; }

  /* Account block — Balance over Exp + the account menu. */
  /* Phone shows the pill cluster instead — see .hdr-acct-mob. */
  .hdr-acct { display: none; flex-direction: column; align-items: flex-end; gap: 2px; min-width: 0; }
  .hdr-bal {
    color: var(--hdr-fg);
    font-size: 13px; font-weight: 700; line-height: 1.15;
    white-space: nowrap;
  }
  .hdr-bal b { font-weight: 800; margin-left: 2px; }
  .hdr-exp-row { display: flex; align-items: center; gap: 10px; }
  .hdr-exp {
    color: rgba(0,0,0,.72);
    font-size: 13px; font-weight: 600; line-height: 1.15;
    white-space: nowrap;
  }
  .hdr-exp b { font-weight: 800; margin-left: 2px; }

  /* ── BAND 2 ── */
  .hdr-below {
    background: #fff;
    /* 16px sides so the search pill measures 358 on a 390pt phone. The band's
       other rows share the padding, so the sign-in buttons and the ticker stay
       flush with the pill rather than overhanging it by 4px each side. */
    padding: 10px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  /* <Marquee> returns null when the brand has no active alert, so this must not
     be given a fixed height or it holds an empty strip open. */
  .hdr-marq-mob { display: flex; align-items: center; overflow: hidden; }
  .hdr-marq-mob:empty { display: none; }
  .hdr-r2 { width: 100%; }

  .hdr-auth-row { display: flex; gap: 10px; width: 100%; }
  .hdr-auth-desk { display: none; }
  .hdr-auth-btn {
    flex: 1 1 0;
    padding: 12px 10px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: .02em;
    text-transform: uppercase;
    color: #fff;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .hdr-auth-btn:hover  { filter: brightness(1.1); }
  .hdr-auth-btn:active { transform: scale(.98); }
  /* As on the reference: a yellow Log in and a bright green Registration, both
     with the dark ink label. Registration carries a soft green glow so it is
     the one that stands out. */
  .hdr-auth-btn.login { background: var(--cb-yellow, #ffb700); color: var(--cb-ink, #343131); }
  .hdr-auth-btn.reg   {
    background: #00C844;
    color: var(--cb-ink, #343131);
    box-shadow: 0 2px 10px rgba(0,200,68,.35);
  }

  /* Desktop centre + the buttons that only exist there. */
  .hdr-center { display: none; }
  .hdr-rules  { display: none; }
  .hdr-dw     { display: none; }

  /* Mobile cashier row. */
  /* ── Phone-only signed-in chrome ── */
  .hdr-acct-mob { display: flex; align-items: center; gap: 8px; }

  .hdr-inbox { position: relative; display: flex; align-items: center; }
  /*
   * Unread count, as on the reference: a round badge carrying the number, not
   * a bare dot — a dot says "something arrived", the number says how much is
   * waiting, which is the thing worth crossing the bar for.
   *
   * The ring is the band's own white, so the badge reads as sitting ON the
   * envelope rather than being part of it. It stays a circle until the count
   * needs two digits, then grows sideways on its pill radius.
   */
  .hdr-inbox-badge {
    position: absolute;
    top: -3px;
    right: -5px;
    display: flex; align-items: center; justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--cb-green-bright, #00c844);
    box-shadow: 0 0 0 2px #fff;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
  }
  .hdr-inbox-veil { position: fixed; inset: 0; z-index: 60; }
  .hdr-inbox-panel {
    position: absolute;
    top: calc(100% + 10px);
    right: -40px;
    z-index: 61;
    width: 244px;
    max-height: 300px;
    overflow-y: auto;
    padding: 6px;
    background: #fff;
    border: 1px solid rgba(0,0,0,.10);
    border-radius: var(--cb-radius-s, .5rem);
    box-shadow: 0 10px 28px rgba(0,0,0,.20);
  }
  .hdr-inbox-empty {
    margin: 0; padding: 14px 10px;
    font-size: 12.5px; color: var(--cb-text-40, rgba(52,49,49,.4));
    text-align: center;
  }
  .hdr-inbox-item { padding: 9px 10px; border-bottom: 1px solid var(--cb-line, #eeecec); }
  .hdr-inbox-item:last-child { border-bottom: none; }
  .hdr-inbox-title { margin: 0 0 3px; font-size: 12.5px; font-weight: 800; color: var(--cb-ink, #343131); }
  .hdr-inbox-body  { margin: 0; font-size: 11.5px; line-height: 1.4; color: rgba(52,49,49,.72); }

  /* The capsule is the white part; both buttons sit inside it. */
  .hdr-wallet {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 3px 3px 9px;
    background: #fff;
    border-radius: 999px;
  }

  /* Balance — also the account menu's trigger, see UserDropdown. */
  .hdr-pill {
    display: flex; align-items: center; gap: 5px;
    max-width: 118px;
    padding: 0;
    border: none;
    background: none;
    color: var(--cb-ink, #343131);
    font-size: 12.5px; font-weight: 800;
    white-space: nowrap;
    cursor: pointer;
    transition: opacity .15s;
  }
  .hdr-pill:hover { opacity: .7; }
  /*
   * The currency mark: a minted coin with the rupee on it, as on the reference.
   * Drawn here rather than pulled from the reference's own PNG — that is
   * another site's asset, and a header icon should not depend on a third party
   * staying up. The gradient is what makes the disc read as metal instead of a
   * flat dot at 15px.
   */
  .hdr-pill-coin {
    display: flex; align-items: center; justify-content: center;
    width: 15px; height: 15px;
    flex-shrink: 0;
    border-radius: 50%;
    background: linear-gradient(145deg, #ffd257, #e8a200);
    color: #6b4400;
  }
  .hdr-pill-amt   { overflow: hidden; text-overflow: ellipsis; }
  .hdr-pill-caret { color: rgba(52,49,49,.55); flex-shrink: 0; }

  .hdr-plus {
    display: flex; align-items: center; justify-content: center;
    width: 23px; height: 23px;
    flex-shrink: 0;
    border: none; border-radius: 50%;
    background: var(--btn-dep-from, var(--cb-green, #16b14a));
    color: #fff;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .hdr-plus:hover  { filter: brightness(1.08); }
  .hdr-plus:active { transform: scale(.9); }

  /*
   * Account quick-nav, right under band 1.
   *
   * A scroller, not a squeezed row: "Payments history" alone is wider than a
   * quarter of a phone, and with Withdraw and FD in there too any attempt to fit
   * them all would either wrap into two lines or clip the labels to nothing.
   */
  .hdr-quick {
    display: flex;
    align-items: stretch;
    gap: 14px;
    width: 100%;
    padding: 0 10px 9px;
    /*
     * Part of the white band, not a strip under it.
     *
     * It is its own element because band 1 is a three-column flex row with no
     * space for a second line, so this paints the SAME background to close the
     * gap — sitting on .hdr's white ground is what made it read as a separate
     * bar. For the same reason the text colour is spelled out here: --hdr-fg is
     * declared on .hdr-r1, so outside that element it resolves to nothing.
     */
    background: #fff;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .hdr-quick::-webkit-scrollbar { display: none; }
  .hdr-quick button {
    flex: 0 0 auto;
    padding: 0;
    border: none;
    background: none;
    color: var(--cb-ink-strong, #111110);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: .02em;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    opacity: .88;
    transition: opacity .15s;
  }
  .hdr-quick button:hover  { opacity: 1; }
  .hdr-quick button:active { opacity: .6; }

  .hdr-bot { border-top: 1px solid rgba(52,49,49,0.14); }

  /* ── DESKTOP >=1280px ── */
  @media (min-width: 1280px) {
    .hdr-r1 { padding: 10px 24px; gap: 14px; }
    .hdr-logo img { width: auto; height: 46px; }
    .hdr-center { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0; }
    .hdr-below  { display: none; }
    .hdr-acct-mob { display: none; }
    .hdr-quick    { display: none; }
    .hdr-menu   { display: none; }
    .hdr-logo-mob  { display: none; }
    .hdr-logo-desk { display: block; }
    .hdr-left   { min-width: 0; }
    .hdr-right  { flex-shrink: 1; gap: 12px; min-width: 0; }
    .hdr-acct { display: flex; }
    .hdr-bal, .hdr-exp { font-size: 14px; }

    .hdr-auth-desk { display: flex; gap: 8px; }
    .hdr-auth-desk .hdr-auth-btn { flex: 0 0 auto; padding: 9px 18px; font-size: 12px; }

    .hdr-rules {
      display: flex; align-items: center; gap: 5px;
      padding: 7px 14px; border-radius: var(--cb-radius-s, .5rem);
      font-size: 12px; font-weight: 600;
      background: var(--hdr-veil);
      border: none;
      color: var(--hdr-fg);
      cursor: pointer; white-space: nowrap;
      transition: background .2s;
    }
    .hdr-rules:hover { background: var(--hdr-veil-hi); }

    .hdr-dw {
      display: flex; align-items: center; gap: 5px;
      padding: 7px 15px; border-radius: var(--cb-radius-s, .5rem);
      font-size: 12px; font-weight: 600;
      color: #fff; border: none; cursor: pointer; white-space: nowrap;
      transition: filter .2s, transform .1s;
    }
    .hdr-dw:hover  { filter: brightness(1.08); }
    .hdr-dw:active { transform: scale(.96); }
    .hdr-dw.dep { background: var(--btn-dep-from, var(--cb-green,#16b14a)); }
    .hdr-dw.wd  { background: var(--btn-wd-from,  var(--cb-ink,#343131)); }
    .hdr-dw.fd  { background: var(--btn-fd-from,  var(--cb-ink,#343131)); }
  }
`;

export default Header;
